import { useState } from 'react'
import { fbt } from 'fbtee'
import { QrCode, Settings, ShieldCheck } from 'lucide-react'
import { useAuth } from '../../hooks/useAuth'
import { useFriendCode } from '../../hooks/useFriendCode'
import { useRequestsUnread } from '../../hooks/useRequestsUnread'
import { useAdminPendingCount } from '../../hooks/useAdminPendingCount'
import { useTransitionNavigate } from '../../lib/viewTransition'
import { Button } from '../../components/ui/button'
import { CountBadge } from '../../components/CountBadge'
import { InviteFriendDialog } from '../../components/InviteFriendDialog'

function HeroIconButton({
  label,
  onClick,
  badge = 0,
  disabled,
  children,
  testId,
}: {
  label: string
  onClick: () => void
  badge?: number
  disabled?: boolean
  children: React.ReactNode
  testId?: string
}) {
  return (
    <Button
      variant="outline"
      size="icon"
      className="relative size-9"
      onClick={onClick}
      disabled={disabled}
      aria-label={label}
      data-testid={testId}
    >
      {children}
      <CountBadge count={badge} className="absolute -right-1 -top-1" />
    </Button>
  )
}

/** Share-profile QR, Settings and (for admins) the admin console, under the own profile header. */
export function SelfProfileActions() {
  const navigate = useTransitionNavigate()
  const { user, signedInUid, isAdmin } = useAuth()
  const { friendCode } = useFriendCode(signedInUid ? user : null)
  const requestsUnread = useRequestsUnread(false)
  const adminPending = useAdminPendingCount()
  const [qrOpen, setQrOpen] = useState(false)

  return (
    <>
      {signedInUid && (
        <Button
          variant="outline"
          className="h-9 flex-1"
          onClick={() => setQrOpen(true)}
          disabled={!friendCode}
        >
          <QrCode className="size-4" />
          {fbt('Share profile', 'Button on the own profile that shows the friend-invite QR code and link')}
        </Button>
      )}
      {isAdmin && signedInUid && (
        <HeroIconButton
          label={fbt('Admin', 'Accessible label of the admin console button on the own profile')}
          onClick={() => navigate('/admin')}
          badge={adminPending}
        >
          <ShieldCheck className="size-4" />
        </HeroIconButton>
      )}
      <HeroIconButton
        label={fbt('Settings', 'Accessible label of the settings button on the own profile')}
        onClick={() => navigate('/settings')}
        badge={requestsUnread}
        testId="profile-settings-button"
      >
        <Settings className="size-4" />
      </HeroIconButton>
      <InviteFriendDialog open={qrOpen} onOpenChange={setQrOpen} />
    </>
  )
}
